import posthog from "posthog-js";

let initialized = false;
let initPromise = null;

const normalizeId = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const isDev = typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV;
const posthogKey = String(import.meta.env?.VITE_POSTHOG_KEY || "").trim();
const configuredHost = String(import.meta.env?.VITE_POSTHOG_HOST || "").trim();
const proxyOptIn = String(import.meta.env?.VITE_POSTHOG_USE_PROXY || "").
trim().
toLowerCase() === "true";
const isLocalDevHost =
typeof window !== "undefined" && (
window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const directPosthogHost = "https://eu.i.posthog.com";
const defaultPosthogHost = directPosthogHost;
const preferredHost = proxyOptIn ? configuredHost || "/ph" : defaultPosthogHost;

function normalizeHost(host) {
  return String(host || "").trim().replace(/\/+$/, "");
}

async function resolvePosthogHost() {
  const host = normalizeHost(preferredHost);
  if (!host) return directPosthogHost;
  if (!host.startsWith("/")) return host;
  if (!proxyOptIn) return directPosthogHost;
  if (typeof window === "undefined" || !posthogKey) return host;

  try {
    const probeUrl = `${host}/array/${posthogKey}/config.js`;
    const response = await fetch(probeUrl, {
      method: "GET",
      credentials: "omit",
      cache: "no-store"
    });
    if (!response.ok) return directPosthogHost;
    const text = await response.text();
    // Vercel fallback pages are HTML; only accept real JS config payload.
    if (!text || text.includes("<!doctype html") || text.includes("<html")) {
      return directPosthogHost;
    }
    return host;
  } catch {
    return directPosthogHost;
  }
}

const stringifyPreview = (value) => {
  try {
    const str = JSON.stringify(value);
    return str.length > 500 ? `${str.slice(0, 500)}…` : str;
  } catch {
    return "[unserializable payload]";
  }
};

const validateTrackingPayload = (eventName, payload = {}) => {
  if (!isDev) return;
  if (!eventName) return;

  const missing = [];
  if (!normalizeId(payload.projectId)) missing.push("projectId");
  if (!normalizeId(payload.artifactType)) missing.push("artifactType");
  if (!normalizeId(payload.artifactId)) missing.push("artifactId");
  if (!normalizeId(payload.source)) missing.push("source");

  if (Object.prototype.hasOwnProperty.call(payload, "initiativeId")) {
    if (!normalizeId(payload.initiativeId)) missing.push("initiativeId");
  }

  if (missing.length) {
    // eslint-disable-next-line no-console
    void 0;



  }
};

export function initPosthog() {
  if (initialized) return Promise.resolve();
  if (initPromise) return initPromise;
  if (typeof window === "undefined") return Promise.resolve();
  if (!posthogKey) return Promise.resolve();

  initPromise = resolvePosthogHost().
  then((apiHost) => {
    posthog.init(posthogKey, {
      api_host: apiHost,
      capture_pageview: true,
      capture_pageleave: true,
      session_recording: {
        maskAllInputs: true
      }
    });
    initialized = true;
  }).
  finally(() => {
    initPromise = null;
  });

  return initPromise;
}

export function track(event, props) {
  if (!event || typeof window === "undefined") return;
  if (!initialized) {
    void initPosthog().then(() => {
      if (initialized) posthog.capture(event, props || {});
    });
    return;
  }
  if (
  event === "workflow_step_completed" ||
  event === "artifact_quality_evaluated" ||
  event === "research_gap_detected")
  {
    validateTrackingPayload(event, props || {});
  }
  posthog.capture(event, props || {});
}

export function trackArtifact(event, payload = {}) {
  const projectId = normalizeId(payload.projectId);
  const artifactType = normalizeId(payload.artifactType);
  const artifactId = normalizeId(payload.artifactId);
  const source = payload.source;

  if (!projectId || !artifactType || !artifactId || !source) return;

  const finalPayload = {
    projectId,
    initiativeId: normalizeId(payload.initiativeId) || null,
    artifactType,
    artifactId,
    source,
    ...payload
  };
  validateTrackingPayload(event, finalPayload);
  track(event, finalPayload);
}

export function trackLink(event, payload = {}) {
  const projectId = normalizeId(payload.projectId);
  const fromType = normalizeId(payload.fromType);
  const toType = normalizeId(payload.toType);
  const fromId = normalizeId(payload.fromId);
  const toId = normalizeId(payload.toId);
  const source = payload.source || payload.linkMethod;

  if (!projectId || !fromType || !toType || !fromId || !toId || !source) return;

  const finalPayload = {
    projectId,
    initiativeId: normalizeId(payload.initiativeId) || null,
    artifactType: fromType,
    artifactId: fromId,
    source,
    fromType,
    toType,
    fromId,
    toId,
    linkMethod: payload.linkMethod || source,
    ...payload
  };
  validateTrackingPayload(event, finalPayload);
  track(event, finalPayload);
}

export function trackAIOutputModified(payload = {}) {
  trackArtifact("ai_output_modified", {
    ...payload,
    source: "ai_then_human"
  });
}
